import { apiJson } from "../api/http.js";
import { fetchAccountMe, marketplaceTelegramWebappBindPhone } from "../api/client.js";
import {
  cacheGlobalProfileFromAccountMe,
  hasMarketplaceJwtCredentials,
  normalizePhoneDigits,
  saveMarketplaceTokens,
  setTelegramWebAppSessionFlag,
} from "./wicoopProfileStorage.js";

function getTelegramWebApp() {
  if (typeof window === "undefined") return null;
  return window.Telegram?.WebApp || null;
}

export function getTelegramInitData() {
  const tg = getTelegramWebApp();
  const init = tg?.initData;
  return init && String(init).trim() ? String(init) : "";
}

export function isInsideTelegramWebApp() {
  return !!getTelegramInitData();
}

let authInFlight = null;

export async function tryAuthenticateTelegramWebApp() {
  const initData = getTelegramInitData();
  if (!initData) return { ok: false, reason: "no_init_data" };
  if (hasMarketplaceJwtCredentials()) return { ok: true };

  if (!authInFlight) {
    authInFlight = (async () => {
      try {
        const data = await apiJson("/api/account/telegram-webapp/auth/", {
          method: "POST",
          body: JSON.stringify({ init_data: initData }),
        });
        if (data?.access) {
          saveMarketplaceTokens({ access: data.access, refresh: data.refresh });
          setTelegramWebAppSessionFlag();
          return { ok: true };
        }
        return { ok: false, reason: "no_tokens" };
      } catch (e) {
        return { ok: false, reason: e.message || "auth_failed" };
      } finally {
        authInFlight = null;
      }
    })();
  }
  return authInFlight;
}

export async function ensureMarketplaceJwtForTelegramParticipate() {
  if (!isInsideTelegramWebApp()) return { ok: false };
  if (hasMarketplaceJwtCredentials()) return { ok: true };
  return tryAuthenticateTelegramWebApp();
}

/**
 * Вход в профиль из Telegram: requestContact → initData/JWT → привязка телефона.
 */
/**
 * Если JWT есть, но телефон не привязан — запросить контакт в Telegram и привязать.
 * Нужно для списка покупок (участия ищутся по номеру).
 */
export async function ensureMarketplaceAccountHasPhone() {
  if (!hasMarketplaceJwtCredentials()) return { ok: false, reason: "no_jwt" };
  try {
    const me = await fetchAccountMe();
    if (normalizePhoneDigits(me?.phone ?? "")) {
      cacheGlobalProfileFromAccountMe(me);
      return { ok: true, hasPhone: true };
    }
  } catch {
    /* bind below */
  }
  if (!isInsideTelegramWebApp() || !canRequestTelegramContact()) {
    return { ok: false, reason: "no_phone" };
  }
  try {
    const rawPhone = await requestTelegramWebAppContactPhone();
    const me = await marketplaceTelegramWebappBindPhone(rawPhone);
    if (me?.access) {
      saveMarketplaceTokens({ access: me.access, refresh: me.refresh, phone: me.phone });
    }
    cacheGlobalProfileFromAccountMe(me);
    return { ok: true, hasPhone: true, phoneBound: true };
  } catch (e) {
    return { ok: false, reason: e?.message || "bind_failed" };
  }
}

export async function ensureTelegramProfileAuth() {
  if (!isInsideTelegramWebApp()) {
    return { ok: false, reason: "not_telegram" };
  }

  if (hasMarketplaceJwtCredentials()) {
    try {
      const me = await fetchAccountMe();
      if (normalizePhoneDigits(me?.phone ?? "")) {
        cacheGlobalProfileFromAccountMe(me);
        return { ok: true, hasPhone: true };
      }
    } catch {
      /* нужна привязка телефона ниже */
    }
  }

  let rawPhone;
  try {
    rawPhone = await requestTelegramWebAppContactPhone();
  } catch (e) {
    const msg = e?.message || "contact_denied";
    const denied = /не предоставлен|отмен|denied|cancel/i.test(msg);
    return { ok: false, reason: msg, denied };
  }

  if (!hasMarketplaceJwtCredentials()) {
    const auth = await tryAuthenticateTelegramWebApp();
    if (!auth.ok) {
      return { ok: false, reason: auth.reason || "auth_failed" };
    }
  }

  try {
    const me = await marketplaceTelegramWebappBindPhone(rawPhone);
    if (me?.access) {
      saveMarketplaceTokens({ access: me.access, refresh: me.refresh, phone: me.phone });
    }
    cacheGlobalProfileFromAccountMe(me);
    return { ok: true, hasPhone: true, phoneBound: true };
  } catch (e) {
    if (hasMarketplaceJwtCredentials()) {
      try {
        const me = await fetchAccountMe();
        if (normalizePhoneDigits(me?.phone ?? "")) {
          cacheGlobalProfileFromAccountMe(me);
          return { ok: true, hasPhone: true };
        }
      } catch {
        /* ignore */
      }
    }
    return { ok: false, reason: e?.message || "bind_failed" };
  }
}

export function canRequestTelegramContact() {
  const tg = getTelegramWebApp();
  return !!(tg && typeof tg.requestContact === "function");
}

export function requestTelegramWebAppContactPhone() {
  return new Promise((resolve, reject) => {
    const tg = getTelegramWebApp();
    if (!tg || typeof tg.requestContact !== "function") {
      reject(new Error("Telegram requestContact недоступен"));
      return;
    }
    tg.requestContact((sent, event) => {
      if (!sent) {
        reject(new Error("Контакт не предоставлен"));
        return;
      }
      const phone =
        event?.responseUnsafe?.contact?.phone_number ||
        event?.contact?.phone_number ||
        event?.phone_number;
      if (!phone) {
        reject(new Error("Телефон не получен"));
        return;
      }
      resolve(String(phone));
    });
  });
}

function applyTelegramSafeArea(tg) {
  const content = tg.contentSafeAreaInset || {};
  const safe = tg.safeAreaInset || {};
  const root = document.documentElement;
  const top = Number(content.top ?? safe.top ?? 0);
  const bottom = Number(content.bottom ?? safe.bottom ?? 0);
  const left = Number(content.left ?? safe.left ?? 0);
  const right = Number(content.right ?? safe.right ?? 0);

  root.style.setProperty("--tg-safe-top", `${top}px`);
  root.style.setProperty("--tg-safe-bottom", `${bottom}px`);
  root.style.setProperty("--tg-safe-left", `${left}px`);
  root.style.setProperty("--tg-safe-right", `${right}px`);

  if (tg.isFullscreen) {
    root.classList.add("tg-fullscreen");
    root.style.setProperty("--tg-chrome-top", `${top}px`);
  } else {
    root.classList.remove("tg-fullscreen");
    root.style.setProperty("--tg-chrome-top", "5vh");
  }
}

function requestTelegramFullscreen(tg) {
  if (requestTelegramFullscreen.done) return;
  if (typeof tg.requestFullscreen !== "function") return;
  requestTelegramFullscreen.done = true;
  try {
    tg.requestFullscreen();
  } catch {
    /* ignore */
  }
}
requestTelegramFullscreen.done = false;

export function applyTelegramChrome() {
  const tg = getTelegramWebApp();
  if (!tg) return;
  try {
    document.documentElement.classList.add("tg-webapp");
    tg.ready();
    tg.expand();
    applyTelegramSafeArea(tg);
    tg.enableClosingConfirmation();
    if (tg.colorScheme === "dark") {
      document.documentElement.style.colorScheme = "dark";
    }
    const header = tg.themeParams?.header_bg_color || tg.themeParams?.bg_color;
    const bg = tg.themeParams?.bg_color;
    if (header) tg.setHeaderColor?.(header);
    if (bg) tg.setBackgroundColor?.(bg);

    // Fullscreen только после первого кадра и один раз (inline web_app ломается при повторных вызовах).
    window.requestAnimationFrame(() => {
      requestTelegramFullscreen(tg);
      applyTelegramSafeArea(tg);
    });

    const syncViewport = () => {
      if (!tg.isExpanded) tg.expand();
      applyTelegramSafeArea(tg);
    };

    tg.onEvent?.("viewportChanged", syncViewport);
    tg.onEvent?.("fullscreenChanged", () => applyTelegramSafeArea(tg));
    tg.onEvent?.("safeAreaChanged", () => applyTelegramSafeArea(tg));
    tg.onEvent?.("contentSafeAreaChanged", () => applyTelegramSafeArea(tg));
    tg.onEvent?.("fullscreenFailed", () => {
      requestTelegramFullscreen.done = true;
      tg.expand();
      applyTelegramSafeArea(tg);
    });
  } catch {
    /* ignore */
  }
}

export const SUPPORT_TELEGRAM_URL = "https://t.me/nastyawicoop";

/** Подтверждение действия: в Telegram WebApp window.confirm часто не работает. */
export function confirmTelegramAction(message) {
  return new Promise((resolve) => {
    const tg = getTelegramWebApp();
    if (tg && typeof tg.showConfirm === "function") {
      try {
        tg.showConfirm(String(message || ""), (ok) => resolve(!!ok));
        return;
      } catch {
        /* fallback */
      }
    }
    resolve(window.confirm(String(message || "")));
  });
}

export function openTelegramUserChat(url = SUPPORT_TELEGRAM_URL) {
  const tg = getTelegramWebApp();
  try {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(url);
      return;
    }
  } catch {
    /* fallback below */
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

export const BOT_SUPPORT_URL = "https://t.me/wicoopbot?start=support";

/** Поддержка через бота: сворачиваем мини-апп и запускаем в боте сценарий чата с поддержкой. */
export function openBotSupportChat() {
  const tg = getTelegramWebApp();
  try {
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(BOT_SUPPORT_URL);
      // Закрываем мини-апп, чтобы пользователь оказался в чате с ботом.
      try {
        tg.close();
      } catch {
        /* ignore */
      }
      return;
    }
  } catch {
    /* fallback below */
  }
  window.open(BOT_SUPPORT_URL, "_blank", "noopener,noreferrer");
}
